import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import {
  BeforeRenderEventHandler,
  NFormComponent,
  NFormControlTemplateContext,
  NFormValuesChange,
} from '@pebula/nform';
import { Subscription } from 'rxjs';

@Component({
  selector: 'sg-simple-model-editor',
  templateUrl: './simple-model-editor.component.html',
  styleUrls: ['./simple-model-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class SgSimpleModelEditorComponent<T = any> implements AfterViewInit {
  @Input() model!: T;
  @Input() title!: string;
  @Input() subTitle!: string;
  @Input() minHeight!: string;
  @Input() maxHeight!: string;
  @Input() minWidth!: string;
  @Input() maxWidth!: string;
  @Input() actionButtonLabel: string = 'Update';
  @Input() canCommit?: (dynForm: NFormComponent) => boolean;
  @Input() getErrors?: (ctx: NFormControlTemplateContext) => string | undefined;
  @Input() disabledState!: string[];
  @Input() filter!: string[];
  @Input() filterMode: 'include' | 'exclude' = 'exclude';
  @Input() formClass: string = 'sg-simple-model-editor-form';
  @Input() panelClass: string | string[] = '';
  @Output() beforeRender = new EventEmitter<{ event: BeforeRenderEventHandler; dynForm: NFormComponent<T> }>();
  @Output() valueChanges = new EventEmitter<{ event: NFormValuesChange; dynForm: NFormComponent<T> }>();
  @Output() onAction = new EventEmitter<{ result: boolean; model: T }>();
  @ViewChild('tpl', { static: true }) tpl!: TemplateRef<any>;

  isFormInvalid = true;

  private _dynForm: NFormComponent<T> | undefined;
  private statusSubscription: Subscription | undefined;

  get dynForm(): NFormComponent<T> | undefined {
    return this._dynForm;
  }
  set dynForm(value: NFormComponent<T> | undefined) {
    this._dynForm = value;
    this.statusSubscription?.unsubscribe();
    if (value?.form) {
      Promise.resolve().then(() => {
        this.isFormInvalid = value.form.invalid;
        this.cdr.markForCheck();
      });
      this.statusSubscription = value.form.statusChanges.subscribe(() => {
        this.isFormInvalid = value.form.invalid;
        this.cdr.markForCheck();
      });
    }
  }

  dialogRef: MatDialogRef<any> | undefined;

  constructor(
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef
  ) {}

  localCanCommit(dynForm: NFormComponent): boolean {
    return dynForm ? (dynForm.form?.invalid ?? true) : true;
  }

  ngAfterViewInit(): void {
    Promise.resolve().then(() => {
      this.dialogRef = this.dialog.open(this.tpl, {
        minHeight: this.minHeight,
        maxHeight: this.maxHeight,
        minWidth: this.minWidth,
        maxWidth: this.maxWidth,
        panelClass: this.panelClass,
      });
      this.dialogRef.afterClosed().subscribe(() => this.verifyShutdown());
    });
  }

  takeAction(result: boolean): void {
    if (result) {
      this.dynForm.nForm.commitToModel(true);
    }
    this.verifyShutdown(result);
  }

  ngOnDestroy(): void {
    this.statusSubscription?.unsubscribe();
    this.verifyShutdown();
  }

  private verifyShutdown(result = false): void {
    const dialogRef = this.dialogRef;
    if (dialogRef) {
      this.dialogRef = undefined;
      this.onAction.next({ result, model: this.model });
      dialogRef.close();
    }
  }
}
